import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Grid,
  Modal,
} from "antd";
import type { TableColumnsType } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { getOrgUsers } from "../../apiBridge";
import { UserInfo } from "../../interface";
import EditUserModal from "./editUserModal";
import DeleteUserModal from "./deleteUserModal";
import ResetUserModal from "./resetUserModal";
import { useLoaderData, useOutletContext } from "react-router-dom";
import { useOrganization } from "../organization/OrganizationContext";
import OrgTitle from "../organization/orgTitleInfo";

const { Title, Link } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

const dateFormatter = (dateString: string) => {
  // Parse the original date-time string
  const originalDate = new Date(dateString);

  // Get the day, month, and year components
  const day = originalDate.getDate().toString().padStart(2, "0"); // padStart ensures two digits
  const month = (originalDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
  const year = originalDate.getFullYear();

  // Format the date into "dd/mm/yyyy" format
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
};

const UsersTableLayout: React.FC = () => {
  const screens = useBreakpoint();
  const data = useLoaderData() as UserInfo[];
  const user = useOutletContext() as UserInfo;

  const { organization, loading } = useOrganization();
  const [loadingState, setLoadingState] = useState(false);
  const [usersData, setUsersData] = useState<UserInfo[]>(data);
  const [filteredUserData, setFilteredUserData] = useState<UserInfo[]>(data);
  const [searchValue, setSearchValue] = useState("");
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isResetUserModalOpen, setIsResetUserModalOpen] = useState(false);
  const [concernedUser, setConcernedUser] = useState<UserInfo>();
  const [triggerTableReload, setTriggerTableReload] = useState(false);

  useEffect(() => {
    if (triggerTableReload && organization?.ID) {
      const getUsersFromOrganisation = async () => {
        const res = await getOrgUsers({ organization: organization.ID });
        setLoadingState(false);
        setTriggerTableReload(false);
        if (res.status === 200) {
          const temp = await res.json();
          const data: UserInfo[] = temp.organization_users;
          
          setUsersData(data);
        }
      };
      getUsersFromOrganisation();
    }
  }, [triggerTableReload, organization?.ID]);

  useEffect(() => {
    if (usersData.length > 0) {
      if (searchValue) {
        const temp = usersData.filter(
          (user) =>
            user.username.includes(searchValue) ||
            user.alias.includes(searchValue)
        );
        setFilteredUserData(temp);
      } else {
        setFilteredUserData(usersData);
      }
    }
  }, [usersData, searchValue]);

  if (loading) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: 4,
          boxShadow: "0 0 8px 0 #cbcbcb",
          padding: 20,
          textAlign: "center"
        }}
      >
        <Title level={4}>Loading...</Title>
      </div>
    );
  }

  const onUserSearch = (value: string) => {
    setSearchValue(value);
  };

  const editUser = (record: UserInfo) => {
    setConcernedUser(record);
    setIsEditUserModalOpen(true);
  };

  const deleteUser = (record: UserInfo) => {
    setConcernedUser(record);
    setIsDeleteUserModalOpen(true);
  };

  const canViewUserEdit = (record: UserInfo) => {
    if (user?.role === 'Owner') {
      return true;
    } else if (user?.role === 'GameMaster') {
      if (record.role === 'User') {
        return true;
      }
    } 

    return false;
  }

  const columns: TableColumnsType<UserInfo> = [
    {
      title: "Username",
      key: "username",
      dataIndex: "username",
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (text) => (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchValue]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ),
    },
    {
      title: "Display Name",
      key: "alias",
      dataIndex: "alias",
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (text) => (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchValue]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ),
    },
    {
      title: "Role",
      key: "role",
      dataIndex: "role",
      sorter: (a, b) => a.role.localeCompare(b.role),
      render: (value) => (
        <Tag color={value === "GameMaster" ? "purple" : undefined}>
          {value === "GameMaster" ? "Game Master" : "Player"}
        </Tag>
      ),
    },
    {
      title: "Created On",
      key: "date_createad",
      dataIndex: "created_at",
      sorter: (a, b) =>
        new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf(),
      render: (value) => dateFormatter(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        if (canViewUserEdit(record)) {
          return (
            <Space size="middle">
              <Link onClick={() => editUser(record)}>
                <EditOutlined />
              </Link>
              <Link type="danger" onClick={() => deleteUser(record)}>
                <DeleteOutlined />
              </Link>
            </Space>
          )
        }
      },
    },
  ];

  return (
    <>
      <OrgTitle title={organization?.Name} dayFromToday={null} />
      <div
        style={{
          background: "white",
          borderRadius: 4,
          boxShadow: "0 0 8px 0 #cbcbcb",
          padding: 20,
        }}
      >
      <Modal
        footer={null}
        maskClosable={false}
        closeIcon={false}
        centered
        open={isCreateUserModalOpen}
        width={420}
      >
          Unable to create this type of account at the moment. Please contact Customer Support at <a href="mailto:info@virtualtkd.gg">info@virtualtkd.gg</a>
      
          <div style={{ marginTop: 10 }}>
            <Button onClick={() => setIsCreateUserModalOpen(false)}>Done</Button>
          </div>
      </Modal>

      <EditUserModal
        isOpen={isEditUserModalOpen}
        controller={setIsEditUserModalOpen}
        user={concernedUser}
        reloadController={setTriggerTableReload}
        resetController={setIsResetUserModalOpen}
        isOwner={user?.role === "Owner"}
      />
      <DeleteUserModal
        isOpen={isDeleteUserModalOpen}
        controller={setIsDeleteUserModalOpen}
        user={concernedUser}
        reloadController={setTriggerTableReload}
      />
      <ResetUserModal
        isOpen={isResetUserModalOpen}
        controller={setIsResetUserModalOpen}
        user={concernedUser}
      />
      <Row gutter={[20, 20]}>
        <Col flex="100px">
          <Title level={5} style={{ margin: 0 }}>
            Members
          </Title>
        </Col>
        {!screens.xs && (
          <Col
            flex="auto"
            span={screens.xs ? undefined : 24}
            style={{ display: "flex", justifyContent: "end" }}
          >
            <Space size="middle">
              {!screens.xs && (
                <Search
                  placeholder="Search User"
                  allowClear
                  disabled={loadingState}
                  onChange={(e) => onUserSearch(e.target.value)}
                  onSearch={onUserSearch}
                  value={searchValue}
                />
              )}
              <Button 
                  type="primary"
                  disabled={loadingState}
                  onClick={() => setIsCreateUserModalOpen(true)}
                >
                  <PlusOutlined /> Create User
                </Button>
            </Space>
          </Col>
        )}
        {screens.xs && (
          <Col span={24}>
            <Row gutter={[20, 20]}>
              <Col flex="auto">
                <Search
                  placeholder="Search User"
                  allowClear
                  disabled={loadingState}
                  onChange={(e) => onUserSearch(e.target.value)}
                  onSearch={onUserSearch}
                />
              </Col>
              <Col flex="32px">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={loadingState}
                  onClick={() => setIsCreateUserModalOpen(true)}
                >
                  {!screens.xs ? "Create User" : ""}
                </Button>
              </Col>
            </Row>
          </Col>
        )}
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={filteredUserData}
            loading={loadingState}
            rowKey="id"
            scroll={{
              x: "max-content",
            }}
          />
        </Col>
      </Row>
      </div>
    </>
  );
};

export default UsersTableLayout;
